import {
  useEffect,
  type FC
} from 'react';
import {
  useNavigate,
  useLocation,
} from 'react-router-dom';
import {
  Box,
  Stack,
  styled,
} from '@mui/joy';
import {
  useModalQueryParam
} from '@shared/lib';
import {
  useBackupPaymentMethods,
  type PaymentMethod
} from '@entities/payments';
import {
  failCrossMark
} from '../assets';
import {
  useWithdrawCoinsFailedParams,
} from '../lib';
import {
  TitleBlock
} from './title-block.component';
import {
  DetailsBlock
} from './details-block.component';
import {
  TryAgainBlock
} from './try-again-block.component';

const PageContainer = styled(Stack)(({ theme }) => ({
  width: '100%',
  paddingBlock: 40,
  paddingInline: 16,
  alignItems: 'center',
  height: 'fit-content',
  justifyContent: 'center',
  [theme.breakpoints.down(491)]: { padding: 16 }
}));

const PageContent = styled(Stack)(({ theme }) => ({
  rowGap: 16,
  padding: 12,
  width: '100%',
  maxWidth: 358,
  borderRadius: 8,
  height: 'fit-content',
  boxSizing: 'border-box',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.common[475],
}));

export const WithdrawCoinsFailedPage: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModalQueryParam();
  const {
    hasDetails,
    paymentMethod,
    checkoutDetails,
    hasPaymentMethod,
  } = useWithdrawCoinsFailedParams();
  const {
    backupPaymentMethods
  } = useBackupPaymentMethods({ paymentMethod });

  const handleTryAgain = (paymentMethod?: Maybe<PaymentMethod>): void => {
    // If payment argument is null or undefined, redirect to lobby
    // else redirect to redeem with chosen payment method
    if (paymentMethod) navigate('/redeem', { state: { paymentMethod, ...checkoutDetails } });
    else navigate(location.state?.from || '/lobby', { replace: true });
  };

  const handleSupportContact = (): void => {
    openModal('chat');
  };

  useEffect(() => {
    if (!hasDetails && !hasPaymentMethod) handleTryAgain();
  }, [hasDetails, hasPaymentMethod,]);

  return (
    <PageContainer>
      <PageContent>
        <Box
          width={100}
          height={104}
          component='img'
          src={failCrossMark.default}
        />
        <TitleBlock onSupportContact={handleSupportContact} />
        <DetailsBlock
          accountNumber={checkoutDetails.accountNumber}
          purchaseAmount={checkoutDetails.purchaseAmount}
        />
        <TryAgainBlock
          onTryAgain={handleTryAgain}
          backupPaymentMethods={backupPaymentMethods}
        />
      </PageContent>
    </PageContainer>
  );
};